<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { CopilotSessionBatchStatusEnum } from "~/utils/enums/copilot/chat.enums";
import { CREATE_SESSION_BATCH_HEADLINE_ICONS } from "~/utils/helpers/copilot/tool-action";
import type { ICreateSessionBatchResult } from "~/utils/interfaces/copilot-interfaces";

/**
 * Footer of a `createSession` card once a batch has run: one line per period, in the order
 * proposed. Green stands for the whole lot only — a partial batch reads as a warning, and a
 * failure ends on the check to make before any retry.
 */
defineProps<{
  result: ICreateSessionBatchResult;
}>();
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <SvgIcon
        :name="CREATE_SESSION_BATCH_HEADLINE_ICONS[result.status]"
        class="h-[16px] w-[16px] shrink-0"
        :class="{
          'text-medium-green-color': result.status === CopilotSessionBatchStatusEnum.CREATED,
          'text-primary-orange-color': result.status === CopilotSessionBatchStatusEnum.PARTIAL,
          'text-primary-red-color': result.status === CopilotSessionBatchStatusEnum.FAILED,
        }"
      />
      <span class="body_regular text-primary-color">{{ result.headline }}</span>
    </div>

    <ul class="flex flex-col gap-1">
      <li v-for="(row, index) of result.rows" :key="`${row.label}-${index}`" class="flex flex-col gap-0.5">
        <div class="flex items-center gap-2">
          <SvgIcon
            :name="row.created ? 'check' : 'warning_error'"
            class="h-[16px] w-[16px] shrink-0"
            :class="row.created ? 'text-medium-green-color' : 'text-primary-red-color'"
          />
          <span class="body_med text-primary-color">{{ row.label }}</span>
        </div>
        <span v-if="row.message" class="meta pl-6 text-secondary-color">{{ row.message }}</span>
      </li>
    </ul>

    <span v-if="result.advice" class="meta text-secondary-color">{{ result.advice }}</span>
  </div>
</template>
